import React, {useState, useContext, useEffect, useRef, useMemo, useCallback} from "react";
import {
  Typography,
  Grid,
  Box,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  Slide,
  Skeleton,
  Chip,
  Divider,
  Stack,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Button,
  TextField,
  Collapse,
} from "@mui/material";
import CustomButton from "../../common/Button/CustomButton";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CartItemsContext from "../../../context/cartItems/CartItemsContext";
import UserProfileContext from "../../../context/userProfile/UserProfileContext";
import LikedItemsContext from "../../../context/likedItems/LikedItemsContext";
import StarRating from "./../Products/StarRating";
import {useNavigate} from "react-router-dom";
import PlaceOrder from "../PlaceOrder/PlaceOrder";
import CustomisedSnackbar from "../../common/Snackbar/CustomisedSnackbar";
import RemoveItemDialog from "./RemoveItemDialog";
import ProductsAPI from "../../../api/firebase/ProductsAPI";
import CouponsAPI from "../../../api/firebase/CouponsAPI";
import StoreSettingsAPI from "../../../api/firebase/StoreSettingsAPI";

import metaPixel from "../../../utils/analytics/metaPixel";

const SlideTransition = React.forwardRef(function SlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const DEFAULT_FREE_SHIPPING_THRESHOLD = Number(process.env.REACT_APP_FREE_SHIPPING_ABOVE) || 500;

const Cart = () => {
  const [open, setOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarActionType, setSnackbarActionType] = useState("increase");
  const [snackbarTitle, setSnackbarTitle] = useState("Cart Updated");
  const [openDialog, setOpenDialog] = useState(false);
  const snackbarMessage = useRef("");
  const checkoutStarted = useRef(false);
  const handleOpen = () => {
    if (!products.length) return;
    if (!checkoutStarted.current) {
      metaPixel.initiateCheckout(products, finalPayable);
      checkoutStarted.current = true;
    }
    setOpen(true);
  };
  const handleClose = () => {
    checkoutStarted.current = false;
    setOpen(false);
  };

  const cartItemsContext = useContext(CartItemsContext);
  const likedItemsContext = useContext(LikedItemsContext);
  const products = cartItemsContext.state || [];

  const itemToRemoveRef = useRef({id: "", title: ""});
  const [itemToRemove, setItemToRemove] = useState(null);
  const [updatingQty, setUpdatingQty] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const productsAPI = useMemo(() => new ProductsAPI(), []);

  const idToTitle = (id) => {
    const prod = products.find((pro) => pro.id === id);
    return prod ? prod.title : "Product";
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const removeCartItem = (itemId, isMoveToWishlist = false, itemTitle = "") => {
    const currentCart = cartItemsContext.state || [];
    const updated = currentCart.filter((prod) => prod.id !== itemId);
    cartItemsContext.setState(updated);

    if (isMoveToWishlist) {
      setSnackbarSeverity("info");
      setSnackbarActionType("remove");
      setSnackbarTitle("Saved to Wishlist");
      snackbarMessage.current = `Moved ${itemTitle || "item"} to your wishlist`;
    } else {
      setSnackbarSeverity("error");
      setSnackbarActionType("remove");
      setSnackbarTitle("Item Removed");
      snackbarMessage.current = `${itemTitle || "Item"} removed from cart`;
    }
    setOpenSnackbar(true);
  };

  const handleAgreeDialog = () => {
    const target = itemToRemove || itemToRemoveRef.current;
    if (target?.id) {
      removeCartItem(target.id, false, target.title || idToTitle(target.id));
    }
    setOpenDialog(false);
  };

  const handleMoveToWishlist = () => {
    const target = itemToRemove || itemToRemoveRef.current;
    if (target?.id) {
      const currentLiked = likedItemsContext?.state || [];
      const alreadyLiked = currentLiked.some((l) => l.id === target.id);
      if (!alreadyLiked) {
        likedItemsContext?.setState?.([target, ...currentLiked]);
      }
      removeCartItem(target.id, true, target.title || idToTitle(target.id));
    }
    setOpenDialog(false);
  };

  const handleMinusIconClick = (itemId) => {
    setUpdatingQty(true);
    const itemTitle = idToTitle(itemId);
    setSnackbarSeverity("info");
    setSnackbarActionType("decrease");
    setSnackbarTitle("Quantity Decreased");
    snackbarMessage.current = `Removed 1 unit of ${itemTitle}`;
    setTimeout(() => {
      const productIndex = cartItemsContext.state.findIndex(
        (obj) => obj.id === itemId
      );
      if (productIndex !== -1) {
        const products_ = [...cartItemsContext.state];
        const newQty = Math.max(1, (products_[productIndex].quantity || 1) - 1);
        products_[productIndex] = {
          ...products_[productIndex],
          quantity: newQty,
        };
        cartItemsContext.setState(products_);
      }
    }, 200);
  };

  const handlePlusIconClick = (itemId) => {
    setUpdatingQty(true);
    const itemTitle = idToTitle(itemId);
    setSnackbarSeverity("success");
    setSnackbarActionType("increase");
    setSnackbarTitle("Quantity Increased");
    snackbarMessage.current = `Added 1 unit of ${itemTitle}`;
    setTimeout(() => {
      const productIndex = cartItemsContext.state.findIndex(
        (obj) => obj.id === itemId
      );
      if (productIndex !== -1) {
        const products_ = [...cartItemsContext.state];
        const stockLimit = products_[productIndex].unitsInStock || 99;
        const currentQty = products_[productIndex].quantity || 1;
        if (currentQty < stockLimit) {
          products_[productIndex] = {
            ...products_[productIndex],
            quantity: currentQty + 1,
          };
          cartItemsContext.setState(products_);
          metaPixel.addToCart(products_[productIndex]);
        }
      }
    }, 200);
  };

  const removeProduct = (productOrId) => {
    let target = null;
    if (productOrId && typeof productOrId === "object") {
      target = productOrId;
    } else {
      target = products.find((p) => p.id === productOrId) || {
        id: productOrId,
        title: idToTitle(productOrId),
      };
    }
    itemToRemoveRef.current = target;
    setItemToRemove(target);
    setOpenDialog(true);
  };

  useEffect(() => {
    // Fetch latest cart product details from database
    productsAPI.getProducts().then((res) => {
      const allProducts = res.data;
      if (allProducts && products && products.length > 0) {
        const latestDetailedCart = [];
        products.forEach((prod) => {
          if (prod && prod.id) {
            const matched = allProducts.find((pro) => pro.id === prod.id);
            if (matched) {
              latestDetailedCart.push({
                ...matched,
                quantity: prod.quantity || 1,
              });
            } else {
              latestDetailedCart.push(prod);
            }
          }
        });
        cartItemsContext.setState(latestDetailedCart);
      }
    });

    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 250);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (updatingQty) {
      setUpdatingQty(false);
      setOpenSnackbar(true);
    }
  }, [cartItemsContext.state]);

  const userProfileContext = useContext(UserProfileContext);
  const [allCoupons, setAllCoupons] = useState([]);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(DEFAULT_FREE_SHIPPING_THRESHOLD);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");
  const [isCouponsExpanded, setIsCouponsExpanded] = useState(false);
  const lastAutoAppliedCode = useRef(null);

  useEffect(() => {
    const couponsAPI = new CouponsAPI();
    couponsAPI
      .getCoupons()
      .then((res) => {
        if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
          setAllCoupons(res.data);
        } else {
          setAllCoupons([
            {
              id: "WELCOME50",
              couponCode: "WELCOME50",
              amount: 50,
              minShopping: 299,
              description: "Flat ₹50 OFF on your wellness order",
              isActive: true,
            },
            {
              id: "AYURVEDA100",
              couponCode: "AYURVEDA100",
              amount: 100,
              minShopping: 699,
              description: "Flat ₹100 OFF on pure Ayurvedic formulations",
              isActive: true,
            },
            {
              id: "FESTIVE150",
              couponCode: "FESTIVE150",
              amount: 150,
              minShopping: 999,
              description: "Save ₹150 on premium holistic health care",
              isActive: true,
            },
          ]);
        }
      })
      .catch((err) => {
        console.log("Error loading coupons:", err);
        setAllCoupons([
          {
            id: "WELCOME50",
            couponCode: "WELCOME50",
            amount: 50,
            minShopping: 299,
            description: "Flat ₹50 OFF on your wellness order",
            isActive: true,
          },
          {
            id: "AYURVEDA100",
            couponCode: "AYURVEDA100",
            amount: 100,
            minShopping: 699,
            description: "Flat ₹100 OFF on pure Ayurvedic formulations",
            isActive: true,
          },
        ]);
      });
  }, []);

  useEffect(() => {
    new StoreSettingsAPI().getShippingSettings().then((result) => {
      const configuredThreshold = Number(result?.data?.freeShippingAbove);
      if (result?.success && Number.isFinite(configuredThreshold) && configuredThreshold >= 0) {
        setFreeShippingThreshold(configuredThreshold);
        setDeliveryFee(Math.max(0, Number(result.data.deliveryFee) || 0));
      }
    });
  }, []);

  // Order summary calculations
  const totalItems = useMemo(() => {
    return products.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0);
  }, [products]);

  const totalMRP = useMemo(() => {
    return products.reduce((acc, item) => {
      const mrp = Number(item.maxPrice) && Number(item.maxPrice) > Number(item.listPrice)
        ? Number(item.maxPrice)
        : Number(item.listPrice) || 0;
      return acc + mrp * (Number(item.quantity) || 1);
    }, 0);
  }, [products]);

  const totalAmount = useMemo(() => {
    return products.reduce(
      (acc, item) => acc + (Number(item.listPrice) || 0) * (Number(item.quantity) || 1),
      0
    );
  }, [products]);

  const [guestCoupon, setGuestCoupon] = useState(null);
  const userId = userProfileContext?.state?.id || userProfileContext?.state?.uid;
  const couponApplied = userId
    ? userProfileContext.state.couponApplied || null
    : guestCoupon;
  const setCouponApplied = useCallback((coupon) => {
    if (userId) {
      userProfileContext.setState((prev) =>
        (prev?.id || prev?.uid) === userId
          ? { ...prev, couponApplied: coupon }
          : prev
      );
    } else {
      setGuestCoupon(coupon);
    }
  }, [userId, userProfileContext]);
  const couponDiscount = couponApplied ? Number(couponApplied.amount) || 0 : 0;
  const isFreeShipping =
    freeShippingThreshold <= 0 || totalAmount >= freeShippingThreshold;
  const deliveryCharge = isFreeShipping ? 0 : deliveryFee;
  const finalPayable = Math.max(0, totalAmount + deliveryCharge - couponDiscount);
  const totalSavings = Math.max(0, totalMRP - totalAmount);
  const totalSavingsWithCoupon = totalSavings + couponDiscount;
  const savingsPercentWithCoupon =
    totalMRP > 0 ? Math.round((totalSavingsWithCoupon / totalMRP) * 100) : 0;
  const shippingRemaining = Math.max(0, freeShippingThreshold - totalAmount);
  const shippingProgress = freeShippingThreshold <= 0
    ? 100
    : Math.min(100, Math.round((totalAmount / freeShippingThreshold) * 100));

  const availableCoupons = useMemo(() => {
    return allCoupons
      .filter((c) => c.isActive !== false)
      .sort((a, b) => {
        const minA = Number(a.minShopping) || 0;
        const minB = Number(b.minShopping) || 0;
        if (minA !== minB) return minA - minB;
        return (Number(b.amount) || 0) - (Number(a.amount) || 0);
      });
  }, [allCoupons]);

  // Find closest coupon that requires more cart amount
  const nextLockedCoupon = useMemo(() => {
    const locked = availableCoupons
      .filter((c) => (c.minShopping || 0) > totalAmount)
      .sort((a, b) => (a.minShopping || 0) - (b.minShopping || 0));
    return locked.length > 0 ? locked[0] : null;
  }, [availableCoupons, totalAmount]);

  // Best coupon that can be applied right now (highest discount amount, lowest minShopping tiebreaker)
  const bestEligibleCoupon = useMemo(() => {
    const eligible = availableCoupons
      .filter((c) => (c.minShopping || 0) <= totalAmount)
      .sort((a, b) => {
        const amtDiff = (Number(b.amount) || 0) - (Number(a.amount) || 0);
        if (amtDiff !== 0) return amtDiff;
        return (Number(a.minShopping) || 0) - (Number(b.minShopping) || 0);
      });
    return eligible.length > 0 ? eligible[0] : null;
  }, [availableCoupons, totalAmount]);

  // Auto-apply the best applicable coupon on cart order summary
  useEffect(() => {
    // If cart is empty or coupons aren't loaded yet
    if (totalAmount <= 0 || availableCoupons.length === 0) {
      if (couponApplied && totalAmount <= 0) {
        setCouponApplied(null);
      }
      return;
    }

    // 1. If a coupon is currently applied, verify its eligibility
    if (couponApplied) {
      const isStillEligible = totalAmount >= (Number(couponApplied.minShopping) || 0);

      if (!isStillEligible) {
        // Current coupon is no longer eligible (e.g. quantity decreased or item removed)
        if (
          bestEligibleCoupon
        ) {
          setCouponApplied({ ...bestEligibleCoupon });
          lastAutoAppliedCode.current = bestEligibleCoupon.couponCode;
          snackbarMessage.current = `Coupon updated to ${bestEligibleCoupon.couponCode} (Saved ₹${bestEligibleCoupon.amount})`;
          setSnackbarTitle("Coupon Updated");
          setSnackbarSeverity("info");
          setSnackbarActionType("increase");
          setOpenSnackbar(true);
        } else {
          setCouponApplied(null);
          snackbarMessage.current = `Coupon ${couponApplied.couponCode} removed as cart total is below ₹${couponApplied.minShopping}`;
          setSnackbarTitle("Coupon Removed");
          setSnackbarSeverity("warning");
          setSnackbarActionType("decrease");
          setOpenSnackbar(true);
        }
        return;
      }

      // Current coupon is still valid, check if an even better coupon is now unlocked (auto-upgrade)
      if (
        bestEligibleCoupon &&
        Number(bestEligibleCoupon.amount) > Number(couponApplied.amount) &&
        bestEligibleCoupon.couponCode !== couponApplied.couponCode
      ) {
        setCouponApplied({ ...bestEligibleCoupon });
        lastAutoAppliedCode.current = bestEligibleCoupon.couponCode;
        snackbarMessage.current = `Better offer unlocked! Auto-applied ${bestEligibleCoupon.couponCode} (Saved ₹${bestEligibleCoupon.amount})`;
        setSnackbarTitle("Coupon Upgraded");
        setSnackbarSeverity("success");
        setSnackbarActionType("increase");
        setOpenSnackbar(true);
      }
      return;
    }

    // 2. No coupon is currently applied -> Auto apply best eligible coupon
    if (
      bestEligibleCoupon
    ) {
      setCouponApplied({ ...bestEligibleCoupon });
      if (lastAutoAppliedCode.current !== bestEligibleCoupon.couponCode) {
        lastAutoAppliedCode.current = bestEligibleCoupon.couponCode;
        snackbarMessage.current = `Best coupon ${bestEligibleCoupon.couponCode} auto-applied! Saved ₹${bestEligibleCoupon.amount}`;
        setSnackbarTitle("Coupon Applied");
        setSnackbarSeverity("success");
        setSnackbarActionType("increase");
        setOpenSnackbar(true);
      }
    }
  }, [totalAmount, availableCoupons, couponApplied, bestEligibleCoupon, setCouponApplied]);

  const handleApplyCoupon = (coupon) => {
    if (!coupon) return;
    if (totalAmount < (coupon.minShopping || 0)) {
      setCouponError(`Min order value of ₹${coupon.minShopping} required`);
      setSnackbarSeverity("warning");
      setSnackbarActionType("decrease");
      setSnackbarTitle("Coupon Ineligible");
      snackbarMessage.current = `Add ₹${coupon.minShopping - totalAmount} more to use ${coupon.couponCode}`;
      setOpenSnackbar(true);
      return;
    }
    setCouponError("");
    setCouponInput("");
    lastAutoAppliedCode.current = coupon.couponCode;
    setCouponApplied({...coupon});
    setSnackbarSeverity("success");
    setSnackbarActionType("increase");
    setSnackbarTitle("Coupon Applied");
    snackbarMessage.current = `Coupon ${coupon.couponCode} applied! Saved ₹${coupon.amount}`;
    setOpenSnackbar(true);
  };

  const handleManualApply = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    const found = availableCoupons.find(
      (c) =>
        c.couponCode?.toUpperCase() === code || c.id?.toUpperCase() === code
    );
    if (found) {
      handleApplyCoupon(found);
    } else {
      setCouponError("Invalid or expired coupon code");
      setSnackbarSeverity("error");
      setSnackbarActionType("remove");
      setSnackbarTitle("Invalid Coupon");
      snackbarMessage.current = `Coupon "${code}" is invalid or expired`;
      setOpenSnackbar(true);
    }
  };

  const getProductImage = (item) => {
    if (item?.images && Array.isArray(item.images) && item.images.length > 0) {
      const firstImg = item.images[0];
      if (typeof firstImg === "string") return firstImg;
      if (firstImg?.url) return firstImg.url;
    }
    if (item?.image) return item.image;
    return null;
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <Box sx={{width: "100%", pb: {xs: 4, md: 2}}}>
      {/* Top Header Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="h5" component="h1" sx={{fontWeight: 700}}>
            Shopping Cart
          </Typography>
          <Chip
            label={`${totalItems} ${totalItems === 1 ? "item" : "items"}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{fontWeight: 600, borderRadius: "8px"}}
          />
        </Stack>

        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/collections")}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
            "&:hover": {color: "primary.main"},
          }}
        >
          Continue Shopping
        </Button>
      </Box>

      {/* Main 2-Column Responsive Layout */}
      <Grid container spacing={{xs: 2.5, md: 3.5}}>
        {/* LEFT COLUMN: Cart Items & Free Shipping Progress */}
        <Grid item xs={12} md={7.5} lg={8}>
          {/* Free Shipping Progress Banner */}
          <Paper
            elevation={0}
            sx={{
              p: {xs: 1.25, sm: 2},
              mb: {xs: 2, sm: 2.5},
              borderRadius: {xs: "16px", sm: "14px"},
              border: "1px solid",
              borderColor: isFreeShipping ? "#bbf7d0" : "#e2e8f0",
              bgcolor: isFreeShipping ? "#f0fdf4" : "#f8fafc",
              background: isFreeShipping
                ? "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)"
                : {xs: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)", sm: "#f8fafc"},
              boxShadow: {xs: "0 6px 16px rgba(15, 23, 42, 0.06)", sm: "none"},
            }}
          >
            <Stack direction="row" alignItems="center" spacing={{xs: 1.1, sm: 1.5}} sx={{mb: 1.1}}>
              <Box
                sx={{
                  width: {xs: 40, sm: 36},
                  height: {xs: 40, sm: 36},
                  borderRadius: {xs: "12px", sm: "50%"},
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isFreeShipping ? "#dcfce7" : "#e2e8f0",
                  color: isFreeShipping ? "#15803d" : "primary.main",
                  flexShrink: 0,
                  boxShadow: {xs: "0 3px 8px rgba(47, 93, 126, 0.12)", sm: "none"},
                }}
              >
                {isFreeShipping ? (
                  <CheckCircleIcon sx={{fontSize: 20}} />
                ) : (
                  <LocalShippingOutlinedIcon sx={{fontSize: 20}} />
                )}
              </Box>
              <Box sx={{flexGrow: 1}}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: isFreeShipping ? "#166534" : "text.primary",
                    fontSize: {xs: "0.84rem", sm: "0.875rem"},
                    lineHeight: 1.25,
                  }}
                >
                  {isFreeShipping
                    ? "Congratulations! You've unlocked FREE Delivery 🎉"
                    : <>
                        Add <Box component="span" sx={{color: "primary.main", fontWeight: 800}}>₹{shippingRemaining.toLocaleString("en-IN")}</Box> for FREE Delivery
                      </>}
                </Typography>
                <Typography variant="caption" sx={{color: "text.secondary", fontSize: {xs: "0.68rem", sm: "0.75rem"}, lineHeight: 1.25, display: "block", mt: 0.2}}>
                  {isFreeShipping
                    ? "Your order qualifies for zero shipping fees."
                    : `₹${deliveryFee.toLocaleString("en-IN")} delivery fee applies on orders below ₹${freeShippingThreshold}.`}
                </Typography>
              </Box>
            </Stack>

            <LinearProgress
              variant="determinate"
              value={shippingProgress}
              sx={{
                height: {xs: 8, sm: 7},
                borderRadius: 4,
                bgcolor: "#e2e8f0",
                "& .MuiLinearProgress-bar": {
                  bgcolor: isFreeShipping ? "#22c55e" : "primary.main",
                  borderRadius: 4,
                },
              }}
            />
          </Paper>

          {/* Cart Item Cards */}
          {showSkeleton ? (
            <Stack spacing={2}>
              {[1, 2].map((s) => (
                <Paper
                  key={s}
                  elevation={0}
                  sx={{p: 2, borderRadius: "16px", border: "1px solid #e2e8f0"}}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3} sm={2.5}>
                      <Skeleton variant="rounded" width="100%" height={90} sx={{borderRadius: "12px"}} />
                    </Grid>
                    <Grid item xs={9} sm={9.5}>
                      <Skeleton variant="text" width="60%" height={28} />
                      <Skeleton variant="text" width="30%" height={20} />
                      <Skeleton variant="text" width="40%" height={24} />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {products.map((product) => {
                const prodImage = getProductImage(product);
                const hasDiscount =
                  product.maxPrice && Number(product.maxPrice) > Number(product.listPrice);
                const discountPct = hasDiscount
                  ? Math.round(
                      ((product.maxPrice - product.listPrice) / product.maxPrice) * 100
                    )
                  : 0;
                const inStock = (product.unitsInStock || 10) > 0;
                const lowStock = inStock && product.unitsInStock <= 5;
                const itemTotal = (Number(product.listPrice) || 0) * (Number(product.quantity) || 1);

                return (
                  <Paper
                    key={product.id}
                    elevation={0}
                    sx={{
                      p: {xs: 1.75, sm: 2.25},
                      borderRadius: "16px",
                      border: "1px solid #e2e8f0",
                      transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                        borderColor: "#cbd5e1",
                      },
                    }}
                  >
                    <Grid container spacing={{xs: 1.5, sm: 2.5}} alignItems="center">
                      {/* Product Thumbnail */}
                      <Grid item xs={3.5} sm={2.5} md={2.5} lg={2}>
                        <Box
                          onClick={() =>
                            navigate(
                              `/collections/${product?.parentCategory?.id || ""}/${product.id}`
                            )
                          }
                          sx={{
                            width: "100%",
                            paddingTop: "100%", // 1:1 Aspect Ratio
                            position: "relative",
                            borderRadius: "12px",
                            overflow: "hidden",
                            bgcolor: "#f8fafc",
                            border: "1px solid #f1f5f9",
                            cursor: "pointer",
                            "&:hover img": {
                              transform: "scale(1.06)",
                            },
                          }}
                        >
                          {prodImage ? (
                            <Box
                              component="img"
                              src={prodImage}
                              alt={product.title}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                p: 1,
                                transition: "transform 0.3s ease",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.disabled",
                              }}
                            >
                              <Typography variant="caption">No Image</Typography>
                            </Box>
                          )}
                        </Box>
                      </Grid>

                      {/* Product Information */}
                      <Grid item xs={8.5} sm={5} md={5} lg={5.5}>
                        <Box>
                          {/* Stock Status Badge */}
                          <Box sx={{mb: 0.5}}>
                            {inStock ? (
                              lowStock ? (
                                <Chip
                                  label={`Only ${product.unitsInStock} left!`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    bgcolor: "#fef3c7",
                                    color: "#b45309",
                                    borderRadius: "4px",
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="In Stock"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    fontWeight: 700,
                                    bgcolor: "#ecfdf5",
                                    color: "#047857",
                                    borderRadius: "4px",
                                  }}
                                />
                              )
                            ) : (
                              <Chip
                                label="Out of Stock"
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  fontWeight: 700,
                                  bgcolor: "#fee2e2",
                                  color: "#b91c1c",
                                  borderRadius: "4px",
                                }}
                              />
                            )}
                          </Box>

                          {/* Product Title */}
                          <Typography
                            variant="subtitle1"
                            onClick={() =>
                              navigate(
                                `/collections/${product?.parentCategory?.id || ""}/${product.id}`
                              )
                            }
                            sx={{
                              fontWeight: 600,
                              lineHeight: 1.35,
                              color: "text.primary",
                              cursor: "pointer",
                              mb: 0.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              "&:hover": {color: "primary.main"},
                            }}
                          >
                            {product.title}
                          </Typography>

                          {/* Ratings if available */}
                          {product.ratings && product.ratings.length > 0 && (
                            <Box sx={{mb: 0.75}}>
                              <StarRating
                                rating={
                                  Math.round(
                                    product.ratings.reduce((a, b) => a + b, 0) /
                                      product.ratings.length /
                                      0.5
                                  ) * 0.5
                                }
                                showTotalNumber={product.ratings.length}
                                showLabel={false}
                              />
                            </Box>
                          )}

                          {/* Price Row */}
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Typography
                              variant="h6"
                              component="span"
                              sx={{fontWeight: 700, color: "text.primary", fontSize: "1.15rem"}}
                            >
                              &#8377;{Number(product.listPrice).toLocaleString("en-IN")}
                            </Typography>

                            {hasDiscount && (
                              <>
                                <Typography
                                  variant="body2"
                                  component="span"
                                  sx={{
                                    textDecoration: "line-through",
                                    color: "text.disabled",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  &#8377;{Number(product.maxPrice).toLocaleString("en-IN")}
                                </Typography>
                                <Chip
                                  label={`${discountPct}% OFF`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.68rem",
                                    fontWeight: 700,
                                    bgcolor: "#ecfdf5",
                                    color: "#059669",
                                    borderRadius: "4px",
                                  }}
                                />
                              </>
                            )}
                          </Stack>
                        </Box>
                      </Grid>

                      {/* Quantity Stepper & Subtotal */}
                      <Grid
                        item
                        xs={12}
                        sm={4.5}
                        md={4.5}
                        lg={4.5}
                        sx={{
                          display: "flex",
                          flexDirection: {xs: "row", sm: "column"},
                          alignItems: {xs: "center", sm: "flex-end"},
                          justifyContent: "space-between",
                          gap: 1.5,
                          pt: {xs: 1, sm: 0},
                          borderTop: {xs: "1px dashed #f1f5f9", sm: "none"},
                        }}
                      >
                        {/* Pill Stepper + Delete Icon */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              border: "1.5px solid #cbd5e1",
                              borderRadius: "24px",
                              bgcolor: "#f8fafc",
                              px: 0.5,
                              py: "2px",
                            }}
                          >
                            <Tooltip title="Decrease quantity">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={product.quantity <= 1 || updatingQty}
                                  onClick={() => handleMinusIconClick(product.id)}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    color: "text.primary",
                                    "&:hover": {bgcolor: "#e2e8f0"},
                                    "&.Mui-disabled": {color: "#cbd5e1"},
                                  }}
                                  aria-label="Decrease quantity"
                                >
                                  <RemoveIcon sx={{fontSize: 16}} />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Typography
                              sx={{
                                minWidth: 32,
                                textAlign: "center",
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color: "text.primary",
                                userSelect: "none",
                              }}
                            >
                              {product.quantity}
                            </Typography>

                            <Tooltip title="Increase quantity">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={
                                    product.quantity >= (product.unitsInStock || 99) || updatingQty
                                  }
                                  onClick={() => handlePlusIconClick(product.id)}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    color: "text.primary",
                                    "&:hover": {bgcolor: "#e2e8f0"},
                                    "&.Mui-disabled": {color: "#cbd5e1"},
                                  }}
                                  aria-label="Increase quantity"
                                >
                                  <AddIcon sx={{fontSize: 16}} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>

                          {/* Delete Item Button */}
                          <Tooltip title="Remove item">
                            <IconButton
                              size="small"
                              onClick={() => removeProduct(product)}
                              sx={{
                                color: "#94a3b8",
                                bgcolor: "#f8fafc",
                                border: "1px solid #e2e8f0",
                                "&:hover": {
                                  color: "#ef4444",
                                  bgcolor: "#fee2e2",
                                  borderColor: "#fca5a5",
                                },
                              }}
                              aria-label="Delete item"
                            >
                              <DeleteOutlineIcon sx={{fontSize: 18}} />
                            </IconButton>
                          </Tooltip>
                        </Stack>

                        {/* Item Subtotal */}
                        <Box sx={{textAlign: {xs: "right", sm: "right"}}}>
                          <Typography variant="caption" sx={{color: "text.secondary", display: "block"}}>
                            Subtotal
                          </Typography>
                          <Typography variant="subtitle1" sx={{fontWeight: 700, color: "text.primary"}}>
                            &#8377;{itemTotal.toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Grid>

        {/* RIGHT COLUMN: Sticky Order Summary Sidebar */}
        <Grid item xs={12} md={4.5} lg={4}>
          <Box
            sx={{
              position: {xs: "static", md: "sticky"},
              top: 85,
              zIndex: 4,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: {xs: 2.5, sm: 3},
                borderRadius: "18px",
                border: "1px solid #e2e8f0",
                bgcolor: "#ffffff",
                boxShadow: "0 12px 28px rgba(15, 23, 42, 0.07)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                  pb: 1.5,
                  borderBottom: "1px solid #eef2f7",
                }}
              >
                <Box>
                  <Typography variant="h6" sx={{fontWeight: 800, lineHeight: 1.2}}>
                    Order Summary
                  </Typography>
                  <Typography variant="caption" sx={{color: "text.secondary"}}>
                    Review your total before checkout
                  </Typography>
                </Box>
                <Chip
                  label={`${totalItems} ${totalItems === 1 ? "item" : "items"}`}
                  size="small"
                  sx={{fontWeight: 700, bgcolor: "#f1f5f9", color: "#475569", borderRadius: "6px"}}
                />
              </Box>

              <Stack spacing={1.5} sx={{mb: 2.5}}>
                {/* MRP Row */}
                <Box sx={{display: "flex", justifyContent: "space-between"}}>
                  <Typography variant="body2" sx={{color: "text.secondary"}}>
                    Total MRP ({totalItems} {totalItems === 1 ? "item" : "items"})
                  </Typography>
                  <Typography variant="body2" sx={{fontWeight: 600}}>
                    &#8377;{totalMRP.toLocaleString("en-IN")}
                  </Typography>
                </Box>

                {/* Savings / Discount Row */}
                {totalSavings > 0 && (
                  <Box sx={{display: "flex", justifyContent: "space-between"}}>
                    <Typography variant="body2" sx={{color: "#16a34a", fontWeight: 500}}>
                      Discount on MRP
                    </Typography>
                    <Typography variant="body2" sx={{color: "#16a34a", fontWeight: 700}}>
                      -&#8377;{totalSavings.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                )}

                <Box sx={{display: "flex", justifyContent: "space-between"}}>
                  <Typography variant="body2" sx={{color: "text.secondary", fontWeight: 600}}>
                    Cart Value
                  </Typography>
                  <Typography variant="body2" sx={{fontWeight: 700}}>
                    &#8377;{totalAmount.toLocaleString("en-IN")}
                  </Typography>
                </Box>

                {/* Delivery Charges Row */}
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                  <Typography variant="body2" sx={{color: "text.secondary"}}>
                    Delivery Fee
                  </Typography>
                  {deliveryCharge === 0 ? (
                    <Chip
                      label="FREE"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        bgcolor: "#ecfdf5",
                        color: "#059669",
                        borderRadius: "4px",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{fontWeight: 700}}>
                      &#8377;{deliveryCharge.toLocaleString("en-IN")}
                    </Typography>
                  )}
                </Box>

                {/* Coupon Discount Row if applied */}
                {couponApplied && (
                  <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    <Typography variant="body2" sx={{color: "#16a34a", fontWeight: 600}}>
                      Coupon Discount ({couponApplied.couponCode})
                    </Typography>
                    <Typography variant="body2" sx={{color: "#16a34a", fontWeight: 700}}>
                      -&#8377;{couponDiscount.toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{my: 1}} />

                {/* Total Amount Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1.25,
                    borderRadius: "10px",
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                      Total Amount
                    </Typography>
                    <Typography variant="caption" sx={{color: "text.secondary", display: "block"}}>
                      Inclusive of all taxes
                    </Typography>
                  </Box>
                  <Typography variant="h5" sx={{fontWeight: 800, color: "primary.main", letterSpacing: "-0.02em"}}>
                    &#8377;{finalPayable.toLocaleString("en-IN")}
                  </Typography>
                </Box>
              </Stack>

              <CustomButton
                variant="contained"
                size="large"
                fullWidth
                startIcon={<ShoppingCartCheckoutIcon />}
                onClick={handleOpen}
                sx={{
                  display: {xs: "none", sm: "flex"},
                  py: 1.5,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: "1rem",
                  boxShadow: "0 6px 18px rgba(47, 93, 126, 0.25)",
                  background: "linear-gradient(135deg, #2f5d7e 0%, #174563 100%)",
                  mb: 2.5,
                }}
              >
                Proceed to Checkout (&#8377;{finalPayable.toLocaleString("en-IN")})
              </CustomButton>

              {/* Total Savings Celebration Callout */}
              {totalSavingsWithCoupon > 0 && (
                <Box
                  sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: "10px",
                    bgcolor: "rgba(34, 197, 94, 0.1)",
                    border: "1px solid rgba(34, 197, 94, 0.25)",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" sx={{fontWeight: 700, color: "#15803d"}}>
                    🎉 You will save &#8377;{totalSavingsWithCoupon.toLocaleString("en-IN")} ({savingsPercentWithCoupon}%) on this order!
                  </Typography>
                </Box>
              )}

              {/* COLLAPSED COUPONS & OFFERS SECTION */}
              <Box
                sx={{
                  mb: 2.5,
                  borderRadius: "12px",
                  bgcolor: "#f8fafc",
                  border: "1px dashed #cbd5e1",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                }}
              >
                {/* When a coupon is already applied */}
                {couponApplied ? (
                  <Box sx={{ bgcolor: "#f0fdf4" }}>
                    <Box
                      sx={{
                        p: {xs: 1.25, sm: 1.5},
                        background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
                        border: "1px solid #bbf7d0",
                        borderRadius: "11px",
                        boxShadow: "0 5px 16px rgba(22, 163, 74, 0.08)",
                      }}
                    >
                      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
                        <Stack direction="row" alignItems="center" spacing={1.1} sx={{minWidth: 0}}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "10px",
                              bgcolor: "#16a34a",
                              color: "#ffffff",
                              display: "grid",
                              placeItems: "center",
                              flexShrink: 0,
                              boxShadow: "0 4px 10px rgba(22, 163, 74, 0.22)",
                            }}
                          >
                            <CheckCircleIcon sx={{fontSize: 21}} />
                          </Box>
                          <Box sx={{minWidth: 0}}>
                            <Typography variant="caption" sx={{color: "#15803d", fontWeight: 800, letterSpacing: "0.06em", lineHeight: 1.1}}>
                              OFFER UNLOCKED
                            </Typography>
                            <Typography
                              variant="subtitle2"
                              sx={{fontWeight: 800, color: "#14532d", letterSpacing: "0.06em", lineHeight: 1.45}}
                            >
                              {couponApplied.couponCode}
                            </Typography>
                            <Typography variant="caption" sx={{color: "#166534", display: "block", lineHeight: 1.2}}>
                              On orders above &#8377;{Number(couponApplied.minShopping || 0).toLocaleString("en-IN")}
                            </Typography>
                          </Box>
                        </Stack>

                        <Box
                          sx={{
                            minWidth: {xs: 80, sm: 92},
                            px: {xs: 0.75, sm: 1},
                            py: 0.7,
                            textAlign: "center",
                            borderRadius: "9px",
                            bgcolor: "#ffffff",
                            border: "1px solid #bbf7d0",
                            flexShrink: 0,
                          }}
                        >
                          <Typography variant="caption" sx={{display: "block", color: "#15803d", fontWeight: 700, fontSize: "0.63rem", lineHeight: 1.1}}>
                            YOU SAVED
                          </Typography>
                          <Typography variant="subtitle2" sx={{fontWeight: 800, color: "#14532d", fontSize: {xs: "0.88rem", sm: "0.98rem"}, lineHeight: 1.3}}>
                            &#8377;{Number(couponApplied.amount).toLocaleString("en-IN")}
                          </Typography>
                        </Box>
                      </Stack>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setIsCouponsExpanded(!isCouponsExpanded)}
                        endIcon={isCouponsExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        sx={{
                          color: "#166534",
                          fontWeight: 700,
                          fontSize: "0.72rem",
                          textTransform: "none",
                          p: 0,
                          mt: 0.9,
                          minWidth: "auto",
                          "& .MuiButton-endIcon": {ml: 0.2},
                        }}
                      >
                        {isCouponsExpanded ? "Hide offers" : "View all offers"}
                      </Button>
                    </Box>

                    {/* Next locked coupon upsell teaser if another higher coupon exists */}
                    {nextLockedCoupon && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          bgcolor: "#fffbeb",
                          borderTop: "1px dashed #fde68a",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                        }}
                        onClick={() => setIsCouponsExpanded(true)}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#92400e", fontWeight: 600, fontSize: "0.72rem" }}
                        >
                          Add items worth &#8377;{nextLockedCoupon.minShopping - totalAmount} to unlock{" "}
                          <strong>{nextLockedCoupon.couponCode}</strong> (Save &#8377;{nextLockedCoupon.amount})
                        </Typography>
                        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: "#92400e" }} />
                      </Box>
                    )}
                  </Box>
                ) : (
                  /* Collapsed Teaser Header: Only shows "Add items worth for this coupon" */
                  <Box
                    onClick={() => setIsCouponsExpanded(!isCouponsExpanded)}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1.25,
                      "&:hover": {bgcolor: "#f1f5f9"},
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{minWidth: 0, flexGrow: 1}}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "8px",
                          bgcolor: "rgba(47, 93, 126, 0.1)",
                          color: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <LocalOfferOutlinedIcon sx={{fontSize: 18}} />
                      </Box>

                      <Box sx={{minWidth: 0, flexGrow: 1}}>
                        {nextLockedCoupon ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#b45309",
                                fontSize: "0.82rem",
                                lineHeight: 1.3,
                              }}
                            >
                              Add items worth &#8377;{nextLockedCoupon.minShopping - totalAmount} for this coupon
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.72rem",
                                display: "block",
                                mt: 0.25,
                              }}
                            >
                              Unlock <strong>{nextLockedCoupon.couponCode}</strong> to save &#8377;{nextLockedCoupon.amount}
                            </Typography>
                          </>
                        ) : bestEligibleCoupon ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#166534",
                                fontSize: "0.82rem",
                                lineHeight: 1.3,
                              }}
                            >
                              Apply {bestEligibleCoupon.couponCode} to save &#8377;{bestEligibleCoupon.amount}!
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.72rem",
                                display: "block",
                                mt: 0.25,
                              }}
                            >
                              {availableCoupons.length} coupons available &bull; Click to apply
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              fontSize: "0.82rem",
                            }}
                          >
                            Have a coupon code?
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    <IconButton
                      size="small"
                      sx={{color: "text.secondary", p: 0.5}}
                      aria-label={isCouponsExpanded ? "Collapse coupons" : "Expand coupons"}
                    >
                      {isCouponsExpanded ? (
                        <KeyboardArrowUpIcon sx={{fontSize: 20}} />
                      ) : (
                        <KeyboardArrowDownIcon sx={{fontSize: 20}} />
                      )}
                    </IconButton>
                  </Box>
                )}

                {/* Collapsible Content: Expands when clicked */}
                <Collapse in={isCouponsExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{p: 1.5, pt: 0, borderTop: "1px dashed #e2e8f0"}}>
                    {/* Manual Coupon Input Box */}
                    <Box sx={{display: "flex", gap: 1, my: 1.5}}>
                      <TextField
                        placeholder="COUPON CODE"
                        size="small"
                        fullWidth
                        value={couponInput}
                        onChange={(e) => {
                          setCouponInput(e.target.value.toUpperCase());
                          if (couponError) setCouponError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleManualApply();
                          }
                        }}
                        error={Boolean(couponError)}
                        helperText={couponError}
                        inputProps={{
                          style: {
                            textTransform: "uppercase",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            letterSpacing: "0.05em",
                            padding: "7.5px 12px",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            bgcolor: "#ffffff",
                          },
                          "& .MuiFormHelperText-root": {
                            fontSize: "0.7rem",
                            mx: 0.5,
                            mt: 0.5,
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleManualApply}
                        disabled={!couponInput.trim()}
                        sx={{
                          borderRadius: "8px",
                          px: 2,
                          fontWeight: 700,
                          textTransform: "none",
                          fontSize: "0.82rem",
                          boxShadow: "none",
                          height: 35,
                          "&:hover": {boxShadow: "none"},
                        }}
                      >
                        Apply
                      </Button>
                    </Box>

                    {/* Available Coupons List */}
                    <Stack spacing={1}>
                      {availableCoupons.map((coupon) => {
                        const isEligible = totalAmount >= (coupon.minShopping || 0);
                        const isCurrent = couponApplied?.couponCode === coupon.couponCode;
                        const diff = Math.max(0, (coupon.minShopping || 0) - totalAmount);

                        return (
                          <Paper
                            key={coupon.id || coupon.couponCode}
                            elevation={0}
                            sx={{
                              p: 1.25,
                              borderRadius: "10px",
                              border: "1px dashed",
                              borderColor: isCurrent ? "#16a34a" : isEligible ? "#cbd5e1" : "#e2e8f0",
                              bgcolor: isCurrent ? "#f0fdf4" : "#ffffff",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                borderColor: isCurrent ? "#16a34a" : isEligible ? "primary.main" : "#cbd5e1",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                              },
                            }}
                          >
                            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1}}>
                              <Box sx={{flexGrow: 1, minWidth: 0}}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.25}}>
                                  <Typography
                                    sx={{
                                      fontWeight: 800,
                                      fontSize: "0.78rem",
                                      letterSpacing: "0.05em",
                                      color: isCurrent ? "#15803d" : "primary.main",
                                      bgcolor: isCurrent ? "#dcfce7" : "rgba(47, 93, 126, 0.08)",
                                      px: 0.75,
                                      py: "2px",
                                      borderRadius: "4px",
                                      display: "inline-block",
                                    }}
                                  >
                                    {coupon.couponCode}
                                  </Typography>
                                  <Typography variant="body2" sx={{fontWeight: 700, color: isCurrent ? "#15803d" : "text.primary", fontSize: "0.82rem"}}>
                                    Save &#8377;{coupon.amount}
                                  </Typography>
                                </Stack>

                                <Typography variant="caption" sx={{color: "text.secondary", display: "block", lineHeight: 1.25}}>
                                  {coupon.description || `Applicable on orders above ₹${coupon.minShopping}`}
                                </Typography>

                                {!isEligible && (
                                  <Typography
                                    variant="caption"
                                    sx={{color: "#b45309", fontWeight: 600, display: "block", mt: 0.5, fontSize: "0.7rem"}}
                                  >
                                    Add items worth &#8377;{diff} for this coupon
                                  </Typography>
                                )}
                              </Box>

                              {isCurrent ? (
                                <Chip
                                  label="Applied"
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    bgcolor: "#dcfce7",
                                    color: "#15803d",
                                    borderRadius: "6px",
                                    px: 0.5,
                                    flexShrink: 0,
                                  }}
                                />
                              ) : (
                                <Button
                                  size="small"
                                  variant={isEligible ? "outlined" : "text"}
                                  disabled={!isEligible}
                                  onClick={() => handleApplyCoupon(coupon)}
                                  sx={{
                                    borderRadius: "6px",
                                    fontWeight: 700,
                                    fontSize: "0.72rem",
                                    textTransform: "none",
                                    px: 1.25,
                                    py: "2px",
                                    minWidth: "auto",
                                    flexShrink: 0,
                                  }}
                                >
                                  {isEligible ? "Apply" : "Locked"}
                                </Button>
                              )}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>

              {/* Trust Badges */}
              <Stack spacing={1.5} sx={{pt: 1, borderTop: "1px solid #f1f5f9"}}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <SecurityOutlinedIcon sx={{fontSize: 20, color: "primary.main"}} />
                  <Typography variant="caption" sx={{color: "text.secondary", fontWeight: 500}}>
                    Safe and Secure Checkout (Razorpay protected)
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <VerifiedUserOutlinedIcon sx={{fontSize: 20, color: "primary.main"}} />
                  <Typography variant="caption" sx={{color: "text.secondary", fontWeight: 500}}>
                    100% Genuine Ayurvedic Formulations
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <ReplayOutlinedIcon sx={{fontSize: 20, color: "primary.main"}} />
                  <Typography variant="caption" sx={{color: "text.secondary", fontWeight: 500}}>
                    Easy Return & Replacement Assistance
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* MOBILE STICKY BOTTOM CHECKOUT BAR (Shown on screens < md) */}
      <Box
        sx={{
          display: {xs: "block", md: "none"},
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: "rgba(255, 255, 255, 0.96)",
          backdropFilter: "blur(12px)",
          borderTop: "1px solid #e2e8f0",
          boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.08)",
          px: 2,
          py: 1.25,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            maxWidth: 600,
            mx: "auto",
          }}
        >
          {/* Price & Savings summary */}
          <Box>
            <Typography variant="caption" sx={{color: "text.secondary", display: "block", lineHeight: 1}}>
              Total Amount
            </Typography>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography variant="h6" sx={{fontWeight: 800, color: "text.primary", lineHeight: 1.3}}>
                &#8377;{finalPayable.toLocaleString("en-IN")}
              </Typography>
              {totalSavingsWithCoupon > 0 && (
                <Chip
                  label={`Save ₹${totalSavingsWithCoupon.toLocaleString("en-IN")}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: "#ecfdf5",
                    color: "#059669",
                    borderRadius: "4px",
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Place order CTA */}
          <CustomButton
            variant="contained"
            size="medium"
            startIcon={<ShoppingCartCheckoutIcon />}
            onClick={handleOpen}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "0.95rem",
              boxShadow: "0 4px 12px rgba(47, 93, 126, 0.3)",
            }}
          >
            Checkout
          </CustomButton>
        </Box>
      </Box>

      {/* Place Order Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen={isMobile}
        maxWidth="md"
        fullWidth
        TransitionComponent={SlideTransition}
        aria-labelledby="place-order-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "24px" },
            m: { xs: 0, sm: 2 },
            height: { xs: "100dvh", sm: "calc(100dvh - 32px)" },
            maxHeight: { xs: "100dvh", sm: "calc(100dvh - 32px)" },
            boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#ffffff",
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(15, 23, 42, 0.6)",
          },
        }}
      >
        <PlaceOrder
          handleClose={handleClose}
          items={products}
          appliedCoupon={couponApplied}
          shippingCost={deliveryCharge}
        />
      </Dialog>

      {/* Notifications Snackbar */}
      <CustomisedSnackbar
        message={snackbarMessage.current}
        title={snackbarTitle}
        severity={snackbarSeverity}
        actionType={snackbarActionType}
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
        autoHideDuration={2400}
      />

      {/* Item Removal Confirmation Dialog */}
      <RemoveItemDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onConfirmRemove={handleAgreeDialog}
        onMoveToWishlist={handleMoveToWishlist}
        item={itemToRemove || itemToRemoveRef.current}
      />
    </Box>
  );
};

export default Cart;
